import { ColumnState } from "ag-grid-community";
import { create } from "zustand";

import { defaultSYS1020_R01Dto, SYS1020_R01Dto } from "./Dto_SYS1020_R01";

export interface SYS1020_R01Filter {
    messageSrc: string;
    messageType: string;
}

export interface Store_SYS1020_R01 {
    SYS1020_R01Filter: SYS1020_R01Filter;
    SYS1020_R01PastFilter: SYS1020_R01Filter;
    setSYS1020_R01Filter: (filter: Partial<SYS1020_R01Filter>) => void;
    setSYS1020_R01PastFilter: (filter: Partial<SYS1020_R01Filter>) => void;

    SYS1020_R01List: SYS1020_R01Dto[];
    setSYS1020_R01List: (list: SYS1020_R01Dto[]) => void;
    insertSYS1020_R01List: (data: SYS1020_R01Dto[], valueIndex?: number) => void;
    removeSYS1020_R01List: (valueIndex: number) => void;
    updateSYS1020_R01List: (valueIndex: number, data: SYS1020_R01Dto) => void;

    selectedSYS1020_R01List: string[];
    setSelectedSYS1020_R01List: (idList: string[]) => void;
    addSelectedSYS1020_R01List: (idList: string[]) => void;
    removeSelectedSYS1020_R01List: (idList?: string[]) => void;
    initSelectedSYS1020_R01List: () => void;
    allSelectedSYS1020_R01List: () => void;

    deletedSYS1020_R01List: string[];
    addDeletedSYS1020_R01List: (idList: string[]) => void;
    removeDeletedSYS1020_R01List: (idList?: string[]) => void;

    originalSYS1020_R01List: SYS1020_R01Dto[];
    setOriginalSYS1020_R01List: (list: SYS1020_R01Dto[]) => void;

    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;
    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;
    allCount: number;
    setAllCount: (count: number) => void;
    initData: () => void;
}

export const defaultSYS1020_R01Filter: SYS1020_R01Filter = {
    messageSrc: "",
    messageType: "",
};

const normalizeList = (list: SYS1020_R01Dto[]): SYS1020_R01Dto[] =>
    list.map((item, index) => ({
        ...defaultSYS1020_R01Dto,
        ...item,
        valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
        orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
        orderIndexSeq: item.orderIndexSeq >= 0 ? item.orderIndexSeq : 0,
        inserted: item.inserted ?? false,
        isModified: item.isModified ?? false,
    }));

export const useSYS1020_R01Store = create<Store_SYS1020_R01>((set) => ({
    SYS1020_R01Filter: defaultSYS1020_R01Filter,
    SYS1020_R01PastFilter: defaultSYS1020_R01Filter,
    setSYS1020_R01Filter: (filter) => set((state) => ({
        SYS1020_R01Filter: { ...state.SYS1020_R01Filter, ...filter },
    })),
    setSYS1020_R01PastFilter: (filter) => set((state) => ({
        SYS1020_R01PastFilter: { ...state.SYS1020_R01PastFilter, ...filter },
    })),

    SYS1020_R01List: [],
    setSYS1020_R01List: (list) => set({ SYS1020_R01List: normalizeList(list) }),
    insertSYS1020_R01List: (data, targetValueIndex = -1) => set((state) => {
        const currentList = [...state.SYS1020_R01List];
        const targetPosition = currentList.findIndex((item) => item.valueIndex === targetValueIndex);
        const insertPosition = targetPosition >= 0 ? targetPosition + 1 : currentList.length;
        const maxValueIndex = Math.max(0, ...currentList.map((item) => item.valueIndex));
        const targetRow = targetPosition >= 0 ? currentList[targetPosition] : undefined;

        if (targetRow) {
            currentList.forEach((item) => {
                if (item.orderIndex === targetRow.orderIndex && item.orderIndexSeq > targetRow.orderIndexSeq) {
                    item.orderIndexSeq += data.length;
                }
            });
        }

        const newRows = data.map((item, index) => ({
            ...defaultSYS1020_R01Dto,
            ...item,
            valueIndex: maxValueIndex + index + 1,
            orderIndex: targetRow?.orderIndex ?? currentList.length + index + 1,
            orderIndexSeq: targetRow ? targetRow.orderIndexSeq + index + 1 : 0,
            inserted: true,
            isModified: false,
        }));
        currentList.splice(insertPosition, 0, ...newRows);
        return { SYS1020_R01List: currentList };
    }),
    removeSYS1020_R01List: (valueIndex) => set((state) => ({
        SYS1020_R01List: state.SYS1020_R01List.filter((item) => item.valueIndex !== valueIndex),
        selectedSYS1020_R01List: state.selectedSYS1020_R01List.filter((id) => Number(id) !== valueIndex),
    })),
    updateSYS1020_R01List: (valueIndex, data) => set((state) => ({
        SYS1020_R01List: state.SYS1020_R01List.map((item) =>
            item.valueIndex === valueIndex ? data : item
        ),
    })),

    selectedSYS1020_R01List: [],
    setSelectedSYS1020_R01List: (idList) => set({ selectedSYS1020_R01List: idList }),
    addSelectedSYS1020_R01List: (idList) => set((state) => ({
        selectedSYS1020_R01List: Array.from(new Set([...state.selectedSYS1020_R01List, ...idList])),
    })),
    removeSelectedSYS1020_R01List: (idList) => set((state) => ({
        selectedSYS1020_R01List: idList
            ? state.selectedSYS1020_R01List.filter((id) => !idList.includes(id))
            : [],
    })),
    initSelectedSYS1020_R01List: () => set({ selectedSYS1020_R01List: [] }),
    allSelectedSYS1020_R01List: () => set((state) => ({
        selectedSYS1020_R01List: state.SYS1020_R01List.map((item) => item.valueIndex.toString()),
    })),

    deletedSYS1020_R01List: [],
    addDeletedSYS1020_R01List: (idList) => set((state) => ({
        deletedSYS1020_R01List: Array.from(new Set([
            ...state.deletedSYS1020_R01List,
            ...idList.filter((id) => {
                const row = state.SYS1020_R01List.find((item) => item.valueIndex === Number(id));
                return row !== undefined && !row.inserted;
            }),
        ])),
    })),
    removeDeletedSYS1020_R01List: (idList) => set((state) => ({
        deletedSYS1020_R01List: idList
            ? state.deletedSYS1020_R01List.filter((id) => !idList.includes(id))
            : [],
    })),

    originalSYS1020_R01List: [],
    setOriginalSYS1020_R01List: (list) => set({ originalSYS1020_R01List: normalizeList(list) }),

    columnState: null,
    setColumnState: (columnState) => set({ columnState }),
    scrollTop: null,
    setScrollTop: (scrollTop) => set({ scrollTop }),
    allCount: 0,
    setAllCount: (allCount) => set({ allCount }),
    initData: () => set({
        SYS1020_R01Filter: defaultSYS1020_R01Filter,
        SYS1020_R01PastFilter: defaultSYS1020_R01Filter,
        SYS1020_R01List: [],
        selectedSYS1020_R01List: [],
        deletedSYS1020_R01List: [],
        originalSYS1020_R01List: [],
        columnState: null,
        scrollTop: null,
        allCount: 0,
    }),
}));
